
import moment from "moment"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { PercentCell } from "../Card"
import { Grid } from "../Table"
import { CustomTooltip, CustomTooltipBar } from "../Tooltip"
import './style.scss'

const COLORS = [
  "#FF8C02", // secondary-orange
  "#FF4001", // primary-orange
  "#004001", // primary-green
  "#0146ad", // tertiary-green
  "#465B4C", // quaternary-green/primary-green-dark-medium
  "#687B71", // moss-primary-green
  "#D31104", // red-blood/warning
  "#001C00", // secondary-green
  "#A8B1AC", // gray-dark-medium
  "#87938A", // gray-dark-medium-two
  "#4F604F", // text-color-medium
  "#99AA99", // gray-dark-medium-three
  "#D5DCD8", // gray-light-medium
  "#FF6B33", // variação orange clara
  "#FF9E66", // variação orange mais clara
  "#1A5A1A", // variação green dark
  "#2E6B3E", // variação green medium
  "#5A7A5A", // variação green gray
  "#7A997A", // variação green light
  "#33663E", // variação moss dark
  "#9CB19C", // variação moss light
  "#B34D40", // variação red orange
  "#8B1E0F", // variação red dark
  "#E84A33", // variação red light
  "#5C6F5C", // variação gray green dark
  "#B8C5B8", // variação gray green light
  "#708470", // variação text medium
  "#3D4D3D", // variação text dark
  "#636E63", // variação text light
  "#1F2F1F"  // variação very dark green
]

// Função para gerar cor consistente baseada no label
const getColorForLabel = (label, uniqueLabels, colors = COLORS) => {
  if (!uniqueLabels || !Array.isArray(uniqueLabels)) {
    return colors[0] || "#FF8C02"
  }
  
  const index = uniqueLabels.indexOf(label)
  return index >= 0 ? colors[index % colors.length] : colors[0]
}

const getVariation = (value, variation, isSum = true) => {
  if (!isSum) {
    return value - variation >= 0 ? value - variation : 0
  }
  return value + variation
}

const getPeriod = (period, type) => {
  if (!type || type.toLowerCase() === "year")
    return moment(period, "DD/MM/YYYY").locale("pt-br").format("MMMM/YYYY")
  if (type.toLowerCase() === "month" || type.toLowerCase() === "day")
    return moment(period, "DD/MM/YYYY").locale("pt-br").format("DD/MMMM")
  if (type.toLowerCase() === "week")
    return `Semana ${moment(period, "DD/MM/YYYY").locale("pt-br").isoWeek()} de ${moment(
      period,
      "DD/MM/YYYY",
    ).format("YYYY")}`
}

export const BarChartVariation = ({ data, hasVariation, period = "Year", labelValue, uniqueLabels, syncColors, ...props }) => {
  const [formattedData, setFormattedData] = useState([])

  useEffect(() => {
    if (data) {
      const splittedFields = data?.fields.map(item => {
        return data?.codes.map(({code}) => {
          const splitted = `${item[code]}`.split("|") || [];

          return splitted.map(value => ({
            ...item,
            [code]: value
          }))
        }).flat()
      }).flat();


      const newData = splittedFields
        ?.filter((item) => item.inFilter)
        .map((item, idx) => {
          const isCount = data?.groupBy && data?.groupBy.toLowerCase() === 'count';
          let value = parseFloat(`${item[data.value]}`.replace(",", ".")) || 0;

          if(isCount) {
            value = splittedFields.filter(row => row[data.stackedBy] === item[data.stackedBy]).length || 0;
            const dataToCompare = idx > 0 ? splittedFields.slice(0, idx) : [];

            const itemExists = dataToCompare.find(row => row[data.stackedBy] === item[data.stackedBy])

            if(itemExists) {
              return null;
            }
          }

          return ({
            period: getPeriod(item["Inicio"], period),
            year: getPeriod(item["Inicio"], "year"),
            user: {
              id: item["ID"],
              name: item[data.stackedBy],
              namePeriod:
                item[data.stackedBy] + "_" + getPeriod(item["Inicio"] || "00-00-0000", period),
              value,
              numNps: item["NumNPS"] || "",
              positiveVariation: getVariation(
                parseFloat(`${item[data.value]}`.replace(",", ".")),
                parseFloat(`${item[data.standardDeviation?.code]}`.replace(",", ".")),
              ),
              negativeVariation: getVariation(
                parseFloat(`${item[data.value]}`.replace(",", ".")) || 0,
                parseFloat(`${item[data.standardDeviation?.code]}`.replace(",", ".")) || 0,
                false,
              ),
            },
          })
        }).filter(item => item)

      // Ordenar dados baseado nos uniqueLabels se sincronização estiver ativa
      if (syncColors && uniqueLabels && uniqueLabels.length > 0) {
        newData.sort((a, b) => {
          const indexA = uniqueLabels.indexOf(a.user?.name)
          const indexB = uniqueLabels.indexOf(b.user?.name)
          
          // Se ambos estão na lista, usar a ordem dos uniqueLabels
          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB
          }
          // Se apenas um está na lista, colocar o que está na lista primeiro
          if (indexA !== -1) return -1
          if (indexB !== -1) return 1
          // Se nenhum está na lista, manter ordem alfabética
          return (a.user?.name || "").localeCompare(b.user?.name || "")
        })
      }

      setFormattedData(newData)
    }
  }, [data, period, syncColors, uniqueLabels])

  return (
    <ResponsiveContainer width="100%" height="87%">
      <ComposedChart
        data={formattedData}
        syncId={data?.title}
        margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
        style={{ fontSize: "12px", strokeWidth: 2 }}>
        <CartesianGrid stroke="#f5f5f5" />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: "transparent" }} />
        <YAxis label={{ value: data?.codes[0]?.title, position: "left", angle: "-90" }} />
        <XAxis xAxisId={0} dataKey="user.name" type="category" tick={{ fontSize: 9, angle: 0 }} />
        {period && (
          <XAxis
            xAxisId={1}
            dataKey="period" 
            interval={10}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 9, angle: 0 }}
          />
        )}
        <Bar dataKey={"user.value"}>
          {formattedData &&
            formattedData.map((entry, index) => {
              const color = syncColors && uniqueLabels 
                ? getColorForLabel(entry.user?.name, uniqueLabels, COLORS)
                : COLORS[index % COLORS.length]
              
              
              return <Cell key={`cell-${index}`} fill={color} />
            })}
          <LabelList dataKey="user.value" style={{ fontWeight: "bold", fill: "white" }} />
        </Bar>
        {hasVariation && (
          <Line
            type="monotone"
            dataKey="user.positiveVariation"
            fill="none"
            stroke="red"
            activeDot={{ r: 6 }}
          />
        )}
        {hasVariation && (
          <Line
            type="monotone"
            dataKey="user.negativeVariation"
            fill="none"
            stroke="black"
            activeDot={{ r: 3 }}
          />
        )}
        <Brush
          dataKey="period"
          startIndex={formattedData.length > 10 ? formattedData.length - 6 : 0}
          height={30}
          stroke="#6c757d">
          <ComposedChart data={formattedData}>
            <CartesianGrid stroke="#f5f5f5" />
            <Bar dataKey={"user.value"}>
              {formattedData &&
                formattedData.map((entry, index) => {
                  const color = syncColors && uniqueLabels 
                    ? getColorForLabel(entry.user?.name, uniqueLabels, COLORS)
                    : COLORS[index % COLORS.length]
                  return <Cell key={`cell-${index}`} fill={color} />
                })}
            </Bar>
            {hasVariation && (
              <Line
                type="monotone"
                dataKey="user.positiveVariation"
                fill="none"
                stroke="red"
                activeDot={{ r: 6 }}
              />
            )}
            {hasVariation && (
              <Line
                type="monotone"
                dataKey="user.negativeVariation"
                fill="none"
                stroke="black"
                activeDot={{ r: 4 }}
              />
            )}
          </ComposedChart>
        </Brush>
      </ComposedChart>
    </ResponsiveContainer>
  )
}

export const BarPercent = ({ data, ...props }) => {
  const [formattedData, setFormattedData] = useState([])

  const { noLabels } = props

  useEffect(() => {
    if (data) {
      const options = data.fields.map((item) => item[data.value])
      const answerCount = options.length // 100%
      setFormattedData(() =>
        [...new Set(options)].map((name) => {
          const percent = (
            (options.filter((item) => item === name).length * 100) /
            answerCount
          ).toFixed(1)

          return {
            option: name?.length > 16 ? `${name.slice(0, 13)}...` : name,
            name,
            percent: `${percent} %`,
            value: percent,
          }
        }),
      )
    }
  }, [data])

  return (
    <ResponsiveContainer width="90%" height="100%" debounce={50}>
      <BarChart
        data={formattedData}
        layout="vertical"
        margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
        <CartesianGrid stroke="#f5f5f5" />
        {noLabels ? "" : <Tooltip content={<CustomTooltip />} cursor={{ fill: "transparent" }} />}
        <XAxis hide axisLine={false} type="number" />
        <YAxis
          yAxisId={0}
          dataKey={"option"}
          type="category"
          tickSize={10}
          axisLine={false}
          tickLine={false}
          width={100}
        />
        <YAxis
          hide
          orientation="right"
          yAxisId={1}
          dataKey={"value"}
          type="category"
          axisLine={false}
          tickLine={false}
          tickFormatter={(value) => `${value.toLocaleString()} %`}
          mirror
          width={200}
        />
        <Bar dataKey={"value"} minPointSize={2} barSize={32}>
          <LabelList dataKey="percent" style={{ fontWeight: "bold", fill: "white" }} />
          {formattedData &&
            formattedData.map((d, idx) => {
              return <Cell key={d["option"]} fill={COLORS[idx % COLORS.length]} />
            })}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

export const StackedBarStep = ({ data }) => {
  return <ResponsiveContainer width="100%" height="100%" debounce={50}>
    <BarChart data={data} width={500}>
      <XAxis dataKey="name" />
      <YAxis />
      <Tooltip />
      <Legend />
      <CartesianGrid stroke="#f5f5f5" />
      <Bar dataKey="Período anterior: Nº de atendimentos sem interação" stackId="b" fill="#F49C93" />
      <Bar dataKey="Período atual: Nº de atendimentos sem interação" stackId="a" fill="#ED7C70" />
      <Bar dataKey="Período anterior: Nº de atendimentos com Interação" stackId="b" fill="#F7DC6F"/>
      <Bar dataKey="Período atual: Nº de atendimentos com Interação" stackId="a" fill="#F9D646" />
      <Bar dataKey="Período anterior: Nº de recomendações aplicadas de forma parcial" stackId="b" fill="#AED6F1" />
      <Bar dataKey="Período atual: Nº de recomendações aplicadas de forma parcial" stackId="a" fill="#69BDF6" />
      <Bar dataKey="Período anterior: Nº de recomendações aplicadas de forma total" stackId="b" fill="#71F3D9" />
      <Bar dataKey="Período atual: Nº de recomendações aplicadas de forma total" stackId="a" fill="#57D6BD" />
    </BarChart>
  </ResponsiveContainer>
}

export const NpsBarFilterTrimester = ({ data }) => {
  const hasFilteredData = data[0]?.completeDate
  const label = !hasFilteredData ? "Média trimestral" : "Média"
  return (
    <ResponsiveContainer width="100%" height="100%" debounce={50}>
      <BarChart data={data.map((item) => ({ ...item, [label]: item?.media?.toFixed(2) }))}>
        <XAxis dataKey={!hasFilteredData ? "trimester" : "completeDate"} />
        <YAxis domain={[0, 10]} />
        <Tooltip />
        {!hasFilteredData && <Legend />}
        <CartesianGrid stroke="#f5f5f5" />
        <Bar dataKey={`${label}`} stackId={`${label}`} fill="#6fc9b7" opacity={0.7} />
      </BarChart>
    </ResponsiveContainer>
  )
}

export const NpsBarFilterPeriod= ({ data }) => {
  const renderLegend = (props) => {
    const legendItems = data[0]?.completeDate && data[1]?.completeDate ? [
      { color: "#FFA67C", label: `Média - ${data[0]?.completeDate || "Referencia"}` },
      { color: "#FF8C02", label: `Média - ${data[1]?.completeDate || ""}` },
    ] : [];
  
    return (
      <ul style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        {legendItems.map((item, index) => (
          <li
            key={`item-${index}`}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginLeft: index === 1 ? 10 : 0,
              color: item.color,
            }}
          >
            <div
              style={{
                width: 20,
                height: 15,
                marginRight: 10,
                backgroundColor: item.color,
              }}
            ></div>
            {item.label}
          </li>
        ))}
      </ul>
    );
  };

  const barColors = ["#FFA67C", "#FF8C02"]

  return (
    <ResponsiveContainer width="100%" height="100%" debounce={50}>
      <BarChart data={data.map((item) => ({ ...item, Média: item?.media?.toFixed(2) }))}>
        <XAxis dataKey="completeDate" />
        <YAxis domain={[0, 10]} />
        <Tooltip />
        <Legend content={renderLegend} />
        <CartesianGrid stroke="#f5f5f5" />
        <Bar dataKey="Média" stackId="a">
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={barColors[index]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

export const cellRenderer = ({ value, data, column }) => {
  const ref = data[`Ref: ${column.field}`];
  const percent = ref === 0 ? value > 0 ? 100 : 0 : parseFloat((value / ref) * 100 - 100).toFixed(0)
  return <div className='rgt-cell-inner rgt-text-truncate table-indicator'>
    <PercentCell hasPercent={(percent > 0 || percent < 0)} percent={percent} subPercent={ref} value={value} />
  </div>
}

export const CellAssistanceRenderer = ({ info }) => {
  const { value } = info
  return <div>{value || 0}</div>
}

const columnsVariation = [
  {
    id: 0,
    field: 'name',
    label: 'Etapa',
    width: 'calc(100% - 720px)',
  },
  {
    id: 1,
    field: 'Sem interação',
    label: 'Sem interação',
    width: '180px',
    cellRenderer
  },
  {
    id: 2,
    field: 'Interação',
    label: 'Interação',
    width: '180px',
    cellRenderer
  },
  {
    id: 3,
    field: 'Aplicação Parcial',
    label: 'Aplicação Parcial',
    width: '180px',
    cellRenderer
  },
  {
    id: 4,
    field: 'Aplicação Total',
    label: 'Aplicação Total',
    width: '180px',
    cellRenderer
  },
]

export const StepVariationTable = ({ rows }) => {
  return <Grid columns={columnsVariation} rows={rows} />
}

export const assistanceColumns = [
  {
    id: 2,
    field: 'Data',
    label: 'Data do atendimento',
    cellRenderer: ({ data }) => <div className='rgt-cell-inner rgt-text-truncate'>
      {moment(data.Data.value).format("DD/MM/YYYYY")}
    </div>,
    width: '200px',
  },
  {
    id: 0,
    field: 'Produtor',
    label: 'Nome do produtor atendido',
    width: 'calc(100% - 500px)',
  },
  {
    id: 1,
    field: 'Tecnico',
    label: 'Nome do técnico responsável pelo atendimento',
    width: '200px',
  },
  {
    id: 3,
    field: 'Formulario',
    label: 'Detalhamento do atendimento',
    width: '200px',
  },
]

export const AssistanceGrid = ({ rows }) => {
  const navigate = useNavigate()
  return <Grid columns={assistanceColumns} rows={rows} onRowClick={({ data }) => navigate(`/assistance/${data.Atendimento}/details`, {state: {values: data}})} />
}

export const BarGroup = ({ data }) => {
  const [formattedData, setFormattedData] = useState([])
  const [keyList, setKeyList] = useState([])

  useEffect(() => {
    if (data) {
      let newKeyList = []
      const newData = data.fields.map((field) => {
        const codes = data.columns.map(
          (column) => data.codes.filter(({ code }) => code === column)[0],
        )

        let answerFields = {}

        codes.map((item) => {
          newKeyList = [...new Set([...newKeyList, item.title])]
          answerFields = { ...answerFields, [item.title]: field[item.code] }
        })

        return {
          Produtor: field.Produtor,
          ...answerFields,
        }
      })

      setKeyList(newKeyList)
      setFormattedData(newData)
    }
  }, [data])

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        width={500}
        height={300}
        data={formattedData}
        margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 5,
        }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="Produtor" />
        <YAxis orientation="left" stroke="#8884d8" />
        <Tooltip />
        <Legend />
        {keyList[0] && <Bar dataKey={keyList[0]} fill={COLORS[0 % COLORS.length]} />}
        {keyList[1] && <Bar dataKey={keyList[1]} fill={COLORS[1 % COLORS.length]} />}
        {keyList[2] && <Bar dataKey={keyList[2]} fill={COLORS[2 % COLORS.length]} />}
      </BarChart>
    </ResponsiveContainer>
  )
}

export const BarYear = ({ data, bars, formatterLegend, colors }) => {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        width={500}
        height={400}
        data={data}
        margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
        style={{ fontSize: "12px", strokeWidth: 2 }}>
        <XAxis dataKey="name" />
        <Tooltip content={<CustomTooltipBar journey />} />
        <Legend formatter={(value) => formatterLegend(value)} />
        {bars.map((item, index) => {
          return (
            <>
              <Bar
                dataKey={`${item?.Trilha}[0].value`}
                fill={colors[index % colors.length]}
                barSize={20}
              />
              <Line
                dataKey={`${item?.Trilha}[0].goalPercent`}
                stroke={colors[index % colors.length]}
                label={false}
              />
            </>
          )
        })}
      </ComposedChart>
    </ResponsiveContainer>
  )
}
